export const ComponentPlacementDescriptions = {
  selector:
    "Selector-mode placement: CSS selector for the anchor element the upsell is inserted near. Unset when anchorKey is used.",
  insertType:
    "Selector-mode placement: where, relative to the anchor element, the upsell is inserted (before/after, inside start/end). Unset when anchorKey is used.",
  anchorKey:
    "Anchor-mode placement: the key a merchant gave the Intelligems Content theme app block. The upsell renders inside every block on the page carrying this key. Unset when selector is used.",
  location:
    "Semantic page context this upsell renders in (pdp or cart). Drives the plugin's activation/re-render strategy and labels/groups the upsell in analytics.",
} as const;

export const OnsiteUpsellRuleDescriptions = {
  id: "Stable identifier for this rule. Owns the rule's targeting row(s) in the targeting table.",
  priority:
    "Evaluation order. Lower numbers are checked first; the first rule whose targeting matches wins.",
  action:
    "Which products to load when this rule matches (Shopify recommendations, specific products, collection, or tag).",
  targeting:
    "When this rule shows. Boolean expression over the chosen domain, evaluated to decide whether the upsell renders.",
} as const;

export const OnsiteUpsellConfigDescriptions = {
  type: "Discriminator for the component kind.",
  placement: "Where in the DOM the rendered upsell is injected.",
  template:
    "HTML for the upsell, including all static copy written inline. The per-product card is the markup between {{#each products}} and {{/each}}. May only reference declared slots: product.* data slots and the named slots (atc, variant-select); everything else is literal HTML.",
  maxProductLimit: "Maximum number of products to render.",
  excludeOutOfStock:
    "Exclude products whose variants are all out of stock. Defaults to true.",
  excludeItemsInCart:
    "Exclude products already in the shopper's cart. Defaults to true.",
  excludePdpProduct:
    "On a PDP, exclude the product being viewed. Defaults to true; turn it off when the viewed product belongs in the result, e.g. a bundle built around it.",
  discount: "Optional discount applied to products added from this upsell.",
  rules:
    "Prioritized list of rules. Each pairs an action (which products) with targeting (when to show). v1 ships a single rule.",
  agenticLogicPlacementId:
    "Agentic Logic placement this upsell evaluates through. Absent means the legacy rule engine serves it — clearing the mapping is the per-surface rollback.",
  agenticLogicRuleId:
    "WRITE-SIDE intent: the Agentic Logic rule this upsell should serve. The experience save upserts the placement and the (placement, variation) assignment from it and stamps `agenticLogicPlacementId`, so callers never assemble that pair themselves. Null detaches the component and returns it to the legacy engine. Reads echo the rule currently assigned; the field is never persisted in the stored config.",
} as const;

export const ComponentDtoDescriptions = {
  id: "Server-generated. Read-only.",
  variationId: "References the parent variation this component belongs to.",
  config:
    "The saved upsell: its type, look, products, targeting, and discount.",
} as const;

export const BundleCopyDescriptions = {
  heading: "Headline above the bundle's products.",
  subheading:
    "Optional line under the heading, shown when no discount tier is active.",
  buttonText: "Label on the bundle's single add-to-cart button.",
  rowBadgeLabel:
    "Per-product discount chip. Accepts #placeholders (see BUNDLE_COPY_FIELD_VARIABLES); defaulted when omitted.",
  savedLabel:
    "Footer 'amount saved' badge. Accepts #placeholders (see BUNDLE_COPY_FIELD_VARIABLES); defaulted when omitted.",
  sublineToNextTier:
    "Subheading while adding products unlocks a better tier. Accepts #placeholders (see BUNDLE_COPY_FIELD_VARIABLES); defaulted when omitted.",
  sublineAtTopTier:
    "Subheading once the top tier is reached. Accepts #placeholders (see BUNDLE_COPY_FIELD_VARIABLES); defaulted when omitted.",
} as const;

export const BundleConfigDescriptions = {
  type: "Discriminator for the component kind.",
  placement: "Where in the DOM the rendered bundle is injected.",
  template:
    "HTML for the bundle card. The per-product row is the markup between {{#each products}} and {{/each}}; the surrounding shell holds the copy slots ({{heading}}, {{subheading}}) and the footer the whole selection shares ({{bundle.price}}, {{bundle.comparePrice}}, {{bundle.atc}}). Each row holds {{checkbox}}, {{product.image}}, {{product.url}}, {{product.title}}, {{product.price}}, {{product.compareAtPrice}} and {{variant-select}}. Every slot is required, even when editing. May only reference declared slots; everything else is literal HTML. No `<script>` tags or inline JavaScript.",
  copy: "Shopper-facing text, edited in the panel rather than inline in the template.",
  maxProductLimit: "Number of products the bundle offers.",
  allowItemSelection:
    "Whether the shopper can choose which of the bundle's products to add. When false every rendered product is included; exclusions can still trim how many render, so lower tiers stay reachable.",
  excludeOutOfStock:
    "Exclude products whose variants are all out of stock. Defaults to true.",
  excludeItemsInCart:
    "Exclude products already in the shopper's cart. Defaults to true.",
  excludePdpProduct:
    "On a PDP, exclude the product being viewed. Defaults to true; turn it off when the viewed product belongs in the result, e.g. a bundle built around it.",
  discount:
    "Optional count-tiered discount applied to the products added from this bundle.",
  rules:
    "Prioritized list of rules. Each pairs an action (which products) with targeting (when to show).",
  agenticLogicPlacementId:
    "Agentic Logic placement this bundle selects its products through. Absent means the legacy rule engine serves it — clearing the mapping is the per-surface rollback. The engine chooses PRODUCTS only: a bundle's tiered discount always comes from `discount` below, never from the result envelope, which can only carry one flat value.",
  agenticLogicRuleId:
    "WRITE-SIDE intent: the Agentic Logic rule this bundle should serve. The experience save upserts the placement and the (placement, variation) assignment from it and stamps `agenticLogicPlacementId`, so callers never assemble that pair themselves. Null detaches the component and returns it to the legacy engine. Reads echo the rule currently assigned; the field is never persisted in the stored config. The engine chooses PRODUCTS only \u2014 a bundle's tiers always come from its own `discount`.",
} as const;

export const BundleDiscountDescriptions = {
  discountType:
    "How the bundle discount is expressed: a percentage off, or a fixed amount off each product.",
  discountTiers:
    "The discount ladder. Each tier's minimumProducts is a threshold, so a tier applies at that many selected products or more, and the highest qualifying tier wins — tiers are not cumulative. A selection below every threshold earns nothing, and a flat discount is a single tier at one product.",
  compareAtPriceDisplay:
    "Which reference price to strike through next to the discounted price.",
} as const;
